/**
 * Chart figure for the editor preview — legend, axes, plot and table view.
 *
 * Mirrors Chart\ChartRenderer. The markup matches the server's, so the editor
 * and the published page render from one stylesheet and cannot drift.
 */

import { __, sprintf } from '@wordpress/i18n';
import { valueFraction } from './geometry';
import { formatValue } from './format';
import CartesianPlot from './cartesian-plot';
import DataTable from './data-table';
import RadialPlot from './radial-plot';

export default function ChartFigure( { config } ) {
	const legend = config.showLegend ? <Legend config={ config } /> : null;

	return (
		<>
			<div className="brandy-chart__figure">
				{ config.legendPosition === 'top' && legend }
				{ config.isRadial ? (
					<RadialFrame config={ config } />
				) : (
					<CartesianFrame config={ config } />
				) }
				{ config.legendPosition === 'bottom' && legend }
			</div>
			{ config.table && <DataTable config={ config } /> }
		</>
	);
}

/**
 * A one-line description of the chart for assistive technology.
 *
 * Mirrors ChartData::aria_label().
 *
 * @param {Object} config Normalised config.
 * @return {string} Label.
 */
function ariaLabel( config ) {
	if ( config.label ) {
		return config.label;
	}

	const names = config.series
		.map( ( series ) => series.label )
		.filter( Boolean );

	return sprintf(
		/* translators: 1: chart type, 2: series names, 3: category count. */
		__( '%1$s chart of %2$s across %3$d categories.', 'brandy-blocks' ),
		config.type.charAt( 0 ).toUpperCase() + config.type.slice( 1 ),
		names.length
			? names.join( ', ' )
			: __( 'the values below', 'brandy-blocks' ),
		config.categories.length
	);
}

/**
 * The plot plus its two axes.
 *
 * A two-by-two grid: the axis gutter is an `auto` track, so the browser sizes it
 * to whatever the tick labels turn out to be. Which axis holds the values and
 * which holds the categories swaps with the orientation; the cells do not.
 */
function CartesianFrame( { config } ) {
	const values = config.showValue ? <ValueAxis config={ config } /> : null;
	const categories = config.showCategory ? (
		<CategoryAxis config={ config } />
	) : null;

	return (
		<div className="brandy-chart__scroll">
			<div className="brandy-chart__frame">
				<div className="brandy-chart__gutter">
					{ config.horizontal ? categories : values }
				</div>
				<div
					className="brandy-chart__plot"
					role="img"
					aria-label={ ariaLabel( config ) }
				>
					<CartesianPlot config={ config } />
				</div>
				<div className="brandy-chart__corner" />
				<div className="brandy-chart__footer">
					{ config.horizontal ? values : categories }
				</div>
			</div>
		</div>
	);
}

function RadialFrame( { config } ) {
	return (
		<div
			className="brandy-chart__plot brandy-chart__plot--radial"
			role="img"
			aria-label={ ariaLabel( config ) }
		>
			<RadialPlot config={ config } />
		</div>
	);
}

function ValueAxis( { config } ) {
	const labels = config.scale.ticks.map( ( tick ) =>
		formatValue( tick, config.format )
	);
	const widest = labels.reduce(
		( longest, text ) => ( text.length > longest.length ? text : longest ),
		''
	);

	return (
		<div className="brandy-chart__axis brandy-chart__axis--value">
			{ /*
			 * Every tick below is positioned absolutely and contributes no
			 * intrinsic width, so the `auto` gutter track would collapse to
			 * zero. This copy of the longest label stays in flow — invisible
			 * and zero height — purely so the browser can size the gutter.
			 */ }
			<span className="brandy-chart__tick-sizer" aria-hidden="true">
				{ widest }
			</span>
			{ config.scale.ticks.map( ( tick, index ) => (
				<span
					key={ index }
					className="brandy-chart__tick"
					style={ {
						'--tick-pos': `${
							valueFraction( tick, config.scale ) * 100
						}%`,
					} }
				>
					{ labels[ index ] }
				</span>
			) ) }
		</div>
	);
}

function CategoryAxis( { config } ) {
	return (
		<div
			className={ `brandy-chart__axis brandy-chart__axis--category${
				config.rotate ? ' brandy-chart__axis--rotated' : ''
			}` }
		>
			{ config.categories.map( ( category, index ) => (
				<span key={ index } className="brandy-chart__category">
					<span>{ category }</span>
				</span>
			) ) }
		</div>
	);
}

/**
 * Legend. Present for two or more identities, never for one. The key mirrors
 * the mark it stands for; the label keeps the muted ink token, because a light
 * categorical hue is illegible as text.
 */
function Legend( { config } ) {
	const shape = config.type === 'line' ? 'line' : 'chip';
	const entries = config.isRadial
		? config.categories.map( ( label, index ) => ( {
				label,
				color: ( config.series[ 0 ].sliceColors || [] )[ index ],
		  } ) )
		: config.series.map( ( series ) => ( {
				label: series.label,
				color: series.color,
		  } ) );

	return (
		<ul
			className={ `brandy-chart__legend brandy-chart__legend--${ config.legendPosition }` }
		>
			{ entries.map( ( entry, index ) => (
				<li key={ index } className="brandy-chart__legend-item">
					<span
						className={ `brandy-chart__legend-key brandy-chart__legend-key--${ shape }` }
						style={ { '--key-color': entry.color } }
					/>
					<span className="brandy-chart__legend-label">
						{ entry.label }
					</span>
				</li>
			) ) }
		</ul>
	);
}
